import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { searchResources } from '../../services/api'
import type { SearchParams, SearchResult } from '../../types'
import SearchBar from '../../components/search/SearchBar'
import ScopeToggle from '../../components/search/ScopeToggle'
import ResultCard from '../../components/search/ResultCard'
import RecentSearches, { addRecentSearch } from '../../components/search/RecentSearches'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import HelpPopover from '../../components/help/HelpPopover'
import { Search, X, FileText, FolderOpen, BookOpen, type LucideIcon } from 'lucide-react'

const ENTITY_TYPE_LABELS: Record<string, string> = {
  material: 'Materials',
  method: 'Methods',
  application: 'Applications',
}

const ENTITY_TYPE_COLORS: Record<string, string> = {
  material: 'bg-teal-500/20 text-teal-800 border-teal-200',
  method: 'bg-amber-500/20 text-amber-800 border-amber-200',
  application: 'bg-rose-500/20 text-rose-800 border-rose-200',
}

type Scope = SearchParams['scope']

// scope='all' fuses these three sources server-side (search/handler.py) — group
// the mixed result set back into sections so "All" reads like a real overview
// instead of an undifferentiated list. 'patents' and 'ecosystem' are NOT part
// of 'all' server-side, so they never appear here — they get their own tab.
const SOURCE_GROUP_META: Record<string, { label: string; icon: LucideIcon; scope: Scope }> = {
  inventions: { label: 'Documents', icon: FileText, scope: 'own' },
  shared: { label: 'Shared', icon: FolderOpen, scope: 'shared' },
  journals: { label: 'Journals', icon: BookOpen, scope: 'journals' },
}

export default function SearchResults() {
  // Honour a deep-linked query (`/search?q=quantum`) — the global search box,
  // notification targets and "See all" links route here with ?q=, so the query
  // must execute on load instead of dumping the user on the pre-search state.
  const [urlParams] = useSearchParams()
  const deepLinkQuery = urlParams.get('q') ?? ''

  const [params, setParams] = useState<SearchParams>({
    query: deepLinkQuery,
    scope: 'all',
    embeddings_mode: true,
    size: 20,
    include_entity_facets: true,
  })
  const [submitted, setSubmitted] = useState(!!deepLinkQuery)
  const [activeEntityFilters, setActiveEntityFilters] = useState<Record<string, string[]>>({})

  // Keep the executed query in sync when the URL ?q= changes without a remount
  // (e.g. a second global-search navigation while already on /search).
  useEffect(() => {
    const q = urlParams.get('q') ?? ''
    if (q && q !== params.query) {
      setParams((prev) => ({ ...prev, query: q }))
      setActiveEntityFilters({})
      setSubmitted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams])

  const searchParams = {
    ...params,
    entity_filters: Object.keys(activeEntityFilters).length > 0 ? activeEntityFilters : undefined,
  }

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['search', searchParams],
    queryFn: () => searchResources(searchParams),
    enabled: submitted && !!params.query,
  })

  const handleSearch = (query: string) => {
    setParams((prev) => ({ ...prev, query }))
    setActiveEntityFilters({})
    setSubmitted(true)
    addRecentSearch(query)
  }

  // Real per-source counts from the CURRENT response only — never a
  // fabricated corpus-wide total. When scope='all' each source's count is the
  // number of that source's hits actually returned (each already capped at
  // `size` server-side before the fuse), so it's live data, just scoped to
  // what was fetched.
  const sourceCounts = useMemo(() => {
    if (!data) return null
    const counts: Record<string, number> = {}
    for (const r of data.results) {
      const key = r.source || 'other'
      counts[key] = (counts[key] || 0) + 1
    }
    return counts
  }, [data])

  const scopeCounts = useMemo(() => {
    if (!data) return undefined
    const counts: Partial<Record<Scope, number>> = { [params.scope]: data.total }
    if (params.scope === 'all' && sourceCounts) {
      for (const [source, meta] of Object.entries(SOURCE_GROUP_META)) {
        if (sourceCounts[source]) counts[meta.scope] = sourceCounts[source]
      }
    }
    return counts
  }, [data, params.scope, sourceCounts])

  // Only worth grouping into sections when 'all' actually mixed more than one
  // source — a single-source result set (or a non-'all' scope) renders as the
  // plain flat list it always has.
  const groupedResults = useMemo(() => {
    if (!data || params.scope !== 'all' || !sourceCounts || Object.keys(sourceCounts).length <= 1) {
      return null
    }
    const groups = new Map<string, SearchResult[]>()
    for (const r of data.results) {
      const key = r.source && SOURCE_GROUP_META[r.source] ? r.source : 'other'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(r)
    }
    return Array.from(groups.entries())
  }, [data, params.scope, sourceCounts])

  const toggleEntityFilter = (type: string, value: string) => {
    setActiveEntityFilters((prev) => {
      const current = prev[type] || []
      if (current.includes(value)) {
        const updated = current.filter((v) => v !== value)
        if (updated.length === 0) {
          const rest = { ...prev }
          delete rest[type]
          return rest
        }
        return { ...prev, [type]: updated }
      }
      return { ...prev, [type]: [...current, value] }
    })
  }

  const clearEntityFilters = () => setActiveEntityFilters({})

  const hasActiveFilters = Object.keys(activeEntityFilters).length > 0

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Search"
        description="Search across your projects, documents, patents and journals from one place."
        helpKey="search"
      />

      <div className="space-y-4 mb-6">
        <SearchBar onSearch={handleSearch} initialQuery={params.query} />
        <div className="flex items-center justify-between">
          <ScopeToggle
            scope={params.scope}
            onChange={(scope) => setParams((prev) => ({ ...prev, scope }))}
            counts={scopeCounts}
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={params.embeddings_mode}
                onChange={(e) => setParams((prev) => ({ ...prev, embeddings_mode: e.target.checked }))}
                className="w-4 h-4 text-iflow-500 border-gray-300 rounded focus:ring-iflow-500"
              />
              Semantic search
            </label>
            <HelpPopover
              topic="search-semantic"
              title="Keyword vs semantic search"
              body="Keyword (off) matches exact words in titles and content — fast, but misses synonyms and related concepts. Semantic (on) uses AI embeddings to find conceptually similar results even when they use different terminology, surfacing related research across disciplines."
              label="About semantic search"
            />
          </div>
        </div>
      </div>

      {/* Entity filter chips */}
      {data?.entity_facets && Object.keys(data.entity_facets).length > 0 && (
        <div className="mb-4 space-y-2">
          {hasActiveFilters && (
            <button onClick={clearEntityFilters} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <X size={12} /> Clear filters
            </button>
          )}
          {Object.entries(data.entity_facets).map(([type, facets]) => (
            <div key={type} className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-20 flex-shrink-0">
                {ENTITY_TYPE_LABELS[type] || type}
              </span>
              {facets.slice(0, 8).map((facet) => {
                const isActive = (activeEntityFilters[type + 's'] || []).includes(facet.value)
                const colorClass = ENTITY_TYPE_COLORS[type] || 'bg-sky-500/10 text-gray-700 border-sky-200'
                return (
                  <button
                    key={`${type}-${facet.value}`}
                    onClick={() => toggleEntityFilter(type + 's', facet.value)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all ${
                      isActive ? colorClass + ' ring-2 ring-offset-1 ring-current' : colorClass + ' opacity-70 hover:opacity-100'
                    }`}
                  >
                    {facet.value}
                    <span className="text-[10px] opacity-60">({facet.count})</span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}

      {isLoading && <LoadingSkeleton rows={5} type="list" />}

      {/* UX-027: a failed search must never leave a blank page under the scope bar.
          The query stays in the search box (state is untouched), so retry re-runs it. */}
      {isError && !isLoading && (
        <EmptyState
          icon={Search}
          title="Search is unavailable"
          description="We couldn't reach search. Your query is kept."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="px-4 py-2 rounded-lg bg-iflow-500 text-white text-sm font-medium hover:bg-iflow-600 disabled:opacity-60"
            >
              Try again
            </button>
          }
        />
      )}

      {data && data.results.length === 0 && data.below_threshold && (
        <EmptyState
          icon={Search}
          title={`No close matches for "${params.query}"`}
          description="No close matches for this query. Try different terms or broaden your scope."
        />
      )}

      {data && data.results.length === 0 && !data.below_threshold && (
        <EmptyState
          icon={Search}
          title="No results found"
          description="Try different keywords or broaden your search scope."
        />
      )}

      {data && data.results.length > 0 && (
        <div>
          <p className="text-sm text-gray-500 mb-4">{data.total} results found</p>
          {groupedResults ? (
            <div className="space-y-6">
              {groupedResults.map(([key, items]) => {
                const meta = SOURCE_GROUP_META[key]
                const Icon = meta?.icon ?? Search
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                        <Icon size={14} className="text-iflow-500" />
                        {meta?.label ?? 'Other results'}
                        <span className="text-xs font-normal text-gray-500">({items.length})</span>
                      </h3>
                      {meta && (
                        <button
                          onClick={() => setParams((prev) => ({ ...prev, scope: meta.scope }))}
                          className="text-xs text-iflow-600 hover:text-iflow-700 font-medium"
                        >
                          See all in {meta.label} &rarr;
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {items.map((result, i) => (
                        <ResultCard key={i} result={result} />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {data.results.map((result, i) => (
                <ResultCard key={i} result={result} />
              ))}
            </div>
          )}
        </div>
      )}

      {!submitted && (
        <>
          <RecentSearches onSelect={handleSearch} />
          <EmptyState
            icon={Search}
            title="Search your innovations"
            description="Search across your documents, shared projects, and related journals."
          />
        </>
      )}
    </div>
  )
}
